import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../../global/GlobalBadge";
import GlobalPagination from "../../../../../global/GlobalPagination";
import {
  getMovementPurposes,
  createMovementPurpose,
  updateMovementPurpose,
  deleteMovementPurpose,
} from "../../../api/movementPurposeApi";
import { useToAndFroRole } from "../../../utils/useToAndFroRole";
import { TOS_FROS_CONTROL_SUBMODULE_SLUG } from "../../../utils/toAndFroPermissionSlugs";

/** String values for <select>; map to mst_statuses.id 1 = active, 2 = inactive. */
const PURPOSE_STATUS_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "2", label: "Inactive" },
];

const MovementPurposeManagement = ({ onNotify, onClose: _onClose, onPurposeUpdate }) => {
  const perms = useToAndFroRole(TOS_FROS_CONTROL_SUBMODULE_SLUG);
  const canManage = !perms.permissionsLoading && perms.canManageMasters;
  const canDelete = !perms.permissionsLoading && perms.canDeleteMasters;
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [formData, setFormData] = useState({
    id: null,
    purpose_name: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const formSectionRef = useRef(null);
  const alertRef = useRef(null);

  const ALERT_ID = "tf-purpose-master-alert";

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const fetchPurposes = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await getMovementPurposes({
          page,
          limit: pagination.pageSize,
        });
        const payload = res?.data ?? res;
        const list = payload?.purposes ?? [];
        const pg = payload?.pagination ?? {};
        setPurposes(Array.isArray(list) ? list : []);
        setPagination((prev) => ({
          ...prev,
          total: Number(pg.total ?? 0),
          totalPages: Math.max(1, Number(pg.totalPages ?? 1)),
          page: Number(pg.page ?? page),
        }));
      } catch (error) {
        onNotify?.(error.message, { variant: "error" });
        setPurposes([]);
      } finally {
        setLoading(false);
      }
    },
    [onNotify, pagination.pageSize],
  );

  useEffect(() => {
    fetchPurposes(1);
  }, [fetchPurposes]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFormError(null);

    const name = String(formData.purpose_name || "").trim();
    if (!name) {
      setFormError("Purpose name is required.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    const statusId = Number(formData.status_id) === 2 ? 2 : 1;

    try {
      if (isEditing && formData.id) {
        await updateMovementPurpose(formData.id, {
          purpose_name: name,
          status_id: statusId,
        });
        onNotify?.("Movement purpose updated successfully.", { variant: "success" });
      } else {
        await createMovementPurpose({
          purpose_name: name,
          status_id: statusId,
        });
        onNotify?.("Movement purpose created successfully.", { variant: "success" });
      }
      handleCancel();
      await fetchPurposes(pagination.page);
      onPurposeUpdate?.();
    } catch (error) {
      const msg = error?.message || "Save failed";
      setFormError(msg);
      onNotify?.(msg, { variant: "error" });
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleEdit = (purpose) => {
    setFormData({
      id: purpose.id,
      purpose_name: purpose.purpose_name || "",
      status_id: Number(purpose.status_id) === 2 ? 2 : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleCancel = () => {
    setFormData({ id: null, purpose_name: "", status_id: 1 });
    setFormError(null);
    setIsEditing(false);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteMovementPurpose(itemToDelete.id);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      onNotify?.("Movement purpose deleted successfully.", { variant: "success" });
      await fetchPurposes(pagination.page);
      onPurposeUpdate?.();
    } catch (error) {
      onNotify?.(error.message, { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="ub-master-section">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Movement purpose master
        </h3>
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        {canManage ? (
          <GlobalFormShell
          legend={isEditing ? "Edit movement purpose" : "Add movement purpose"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save purpose" : "Create purpose"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "purpose_name_edit" : "purpose_name"}
            label="Purpose name"
            value={formData.purpose_name}
            onChange={(v) => setFormField("purpose_name", v)}
            required
          />
          <GlobalSelectField
            id={isEditing ? "purpose_status_edit" : "purpose_status"}
            label="Status"
            value={String(formData.status_id)}
            onChange={(v) =>
              setFormField("status_id", Number(v) === 2 ? 2 : 1)
            }
            options={PURPOSE_STATUS_OPTIONS}
          />
        </GlobalFormShell>
        ) : (
          <p className="visitor-workspace__placeholder" style={{ marginBottom: "1rem" }}>
            You have view-only access to movement purposes.
          </p>
        )}
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Movement purpose master table"
        headers={[
          { key: "purpose_name", label: "Purpose name" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Created date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {loading ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              Loading…
            </td>
          </tr>
        ) : (
          purposes.map((purpose) => (
            <tr key={purpose.id}>
              <td style={{ fontWeight: 500 }}>{purpose.purpose_name}</td>
              <td>
                <GlobalBadge active={Number(purpose.status_id) === 1} />
              </td>
              <td>
                {purpose.created_at
                  ? new Date(purpose.created_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="ub-table__col-actions">
                {canManage ? (
                  <div className="ub-table-actions">
                    <GlobalTableActionButton action="edit" onClick={() => handleEdit(purpose)} />
                    {canDelete ? (
                      <GlobalTableActionButton
                        action="delete"
                        onClick={() => handleDeleteClick(purpose)}
                      />
                    ) : null}
                  </div>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))
        )}
        {!loading && purposes.length === 0 ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
              No movement purposes found.
            </td>
          </tr>
        ) : null}
      </GlobalDataTable>

      {pagination.totalPages > 1 ? (
        <div style={{ marginTop: "1rem" }}>
          <GlobalPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={(p) => fetchPurposes(p)}
          />
        </div>
      ) : null}

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove movement purpose <b>{itemToDelete?.purpose_name}</b>? It will be hidden from lists
          when no longer referenced.
        </p>
      </GlobalModal>
    </div>
  );
};

export default MovementPurposeManagement;
